import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Trash2, Star, Save, Eye, Mail } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const KIND_LABELS = {
    job_annual_ready: "Job — Annual return",
    job_gst_ready: "Job — GST return",
    welcome: "Welcome email",
    generic: "Generic",
};

export default function EmailTemplates() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [cloneOpen, setCloneOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/email-templates/");
            setRows(data.rows || []);
            if (!selectedId && (data.rows || []).length) setSelectedId(data.rows[0].id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const selected = rows.find((r) => r.id === selectedId);

    async function saveSelected(patch) {
        try {
            const { data } = await api.patch(`/email-templates/${selectedId}`, patch);
            setRows((rs) => rs.map((r) => (r.id === data.id ? data : r)));
            toast.success("Saved");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function deleteSelected() {
        if (!selected || selected.is_system) return;
        if (!window.confirm(`Delete "${selected.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/email-templates/${selectedId}`);
            toast.success("Deleted");
            const remaining = rows.filter((r) => r.id !== selectedId);
            setRows(remaining);
            setSelectedId(remaining[0]?.id || null);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Settings"
                title="Email templates"
                description="Clone the system defaults to make your own editable versions. Set one as the default for each kind so it's pre-selected when you send from a job card."
                testId="email-templates-header"
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
                {/* Left: list */}
                <div className="rounded-2xl border border-[#EAE7DE] bg-white overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="p-6 text-sm text-[#5E636E]">No templates yet.</div>
                    ) : (
                        <ul className="divide-y divide-[#EAE7DE]" data-testid="et-list">
                            {rows.map((r) => (
                                <li
                                    key={r.id}
                                    onClick={() => setSelectedId(r.id)}
                                    className={"px-3 py-2.5 cursor-pointer hover:bg-zinc-50 " + (selectedId === r.id ? "bg-zinc-100" : "")}
                                    data-testid={`et-row-${r.id}`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <Mail className="h-3.5 w-3.5 text-[#5E636E]" />
                                        <span className="text-sm font-medium truncate">{r.name}</span>
                                        {r.is_default && <Star className="h-3 w-3 fill-amber-500 text-amber-500" />}
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[#5E636E]">
                                        <span>{KIND_LABELS[r.kind] || r.kind}</span>
                                        {r.is_system && <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px]">system</span>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Right: editor */}
                <div className="rounded-2xl border border-[#EAE7DE] bg-white p-4">
                    {!selected ? (
                        <div className="text-sm text-[#5E636E]">Select a template on the left.</div>
                    ) : (
                        <TemplateEditor
                            key={selected.id}
                            template={selected}
                            onSave={saveSelected}
                            onDelete={deleteSelected}
                            onClone={() => setCloneOpen(true)}
                        />
                    )}
                </div>
            </div>

            <CloneDialog
                open={cloneOpen}
                onClose={() => setCloneOpen(false)}
                source={selected}
                onCloned={(t) => {
                    setRows((rs) => [...rs, t]);
                    setSelectedId(t.id);
                    setCloneOpen(false);
                }}
            />
        </div>
    );
}


function TemplateEditor({ template, onSave, onDelete, onClone }) {
    const [subject, setSubject] = useState(template.subject_template || "");
    const [html, setHtml] = useState(template.html_template || "");
    const [name, setName] = useState(template.name || "");
    const [notes, setNotes] = useState(template.notes || "");
    const [tab, setTab] = useState("html"); // html | preview
    const canEdit = !template.is_system;

    const dirty = subject !== template.subject_template || html !== template.html_template
        || name !== template.name || (notes || "") !== (template.notes || "");

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-[11px] text-[#5E636E] uppercase tracking-[0.14em]">{KIND_LABELS[template.kind] || template.kind}</div>
                    <div className="mt-1 flex items-center gap-2">
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={!canEdit}
                            className={`text-lg font-semibold h-9 w-[380px] max-w-full ${canEdit ? "" : "bg-[#FAFAF7]"}`}
                            data-testid="et-name"
                        />
                        {template.is_default && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px]"><Star className="h-3 w-3 fill-amber-500 text-amber-500" /> Default</span>}
                        {template.is_system && <span className="rounded-full bg-zinc-100 text-zinc-700 px-2 py-0.5 text-[11px]">system</span>}
                    </div>
                </div>
                <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" onClick={onClone} className="gap-1" data-testid="et-clone">
                        <Copy className="h-3.5 w-3.5" /> Clone
                    </Button>
                    {canEdit && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onSave({ is_default: !template.is_default })}
                                className="gap-1"
                                data-testid="et-toggle-default"
                            >
                                <Star className={"h-3.5 w-3.5 " + (template.is_default ? "fill-amber-500 text-amber-500" : "")} />
                                {template.is_default ? "Unset default" : "Set as default"}
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => onSave({ name, subject_template: subject, html_template: html, notes })}
                                disabled={!dirty}
                                className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white gap-1"
                                data-testid="et-save"
                            >
                                <Save className="h-3.5 w-3.5" /> Save
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onDelete}
                                className="gap-1 text-red-700 hover:bg-red-50 border-red-200"
                                data-testid="et-delete"
                            >
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div>
                <Label className="text-[11px] text-[#5E636E]">Subject</Label>
                <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={!canEdit}
                    className={`mt-1 ${canEdit ? "" : "bg-[#FAFAF7]"}`}
                    data-testid="et-subject"
                />
            </div>

            <div>
                <div className="flex items-center justify-between">
                    <Label className="text-[11px] text-[#5E636E]">Email body</Label>
                    <div className="flex text-[11px] rounded-full border border-[#E4E4E7] overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setTab("preview")}
                            className={`px-2.5 py-1 ${tab === "preview" ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E]"}`}
                            data-testid="et-view-preview"
                        ><Eye className="inline h-3 w-3 mr-1" /> Preview</button>
                        <button
                            type="button"
                            onClick={() => setTab("html")}
                            className={`px-2.5 py-1 ${tab === "html" ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E]"}`}
                            data-testid="et-view-html"
                        >HTML</button>
                    </div>
                </div>
                {tab === "preview" ? (
                    <div
                        className="mt-1 border border-[#E4E4E7] rounded-md p-4 bg-white max-h-[420px] overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: html }}
                        data-testid="et-preview"
                    />
                ) : (
                    <Textarea
                        rows={14}
                        value={html}
                        onChange={(e) => setHtml(e.target.value)}
                        disabled={!canEdit}
                        className={`mt-1 font-mono text-[12px] ${canEdit ? "" : "bg-[#FAFAF7]"}`}
                        data-testid="et-html"
                    />
                )}
            </div>

            <div>
                <Label className="text-[11px] text-[#5E636E]">Notes (internal, optional)</Label>
                <Textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={!canEdit}
                    className={`mt-1 ${canEdit ? "" : "bg-[#FAFAF7]"}`}
                    data-testid="et-notes"
                />
            </div>

            <div className="rounded-lg bg-[#FAFAF7] border border-[#E4E4E7] p-3 text-[12px] text-[#5E636E]">
                <div className="font-medium text-[#0F1115] mb-1">Available tokens</div>
                Use these anywhere in the subject or body — they&apos;re filled in per send.
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[11px] text-[#0F1115]">
                    <span>{"{{client_name}}"}</span>
                    <span>{"{{client_display_name}}"}</span>
                    <span>{"{{nickname}}"}</span>
                    <span>{"{{contact_email}}"}</span>
                    <span>{"{{period_label}}"}</span>
                    <span>{"{{kind_label}}"}</span>
                    <span>{"{{period_end_date}}"}</span>
                    <span>{"{{due_date}}"}</span>
                    <span>{"{{data_source_label}}"}</span>
                    <span>{"{{data_source_link}}"}</span>
                    <span>{"{{data_source_instructions}}"}</span>
                    <span>{"{{portal_url}}"}</span>
                    <span>{"{{firm_name}}"}</span>
                </div>
            </div>
        </div>
    );
}


function CloneDialog({ open, onClose, source, onCloned }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open && source) setName(`${source.name} (copy)`); }, [open, source]);

    async function submit() {
        if (!source || !name.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/email-templates/${source.id}/clone`, { name: name.trim() });
            onCloned?.(data);
            toast.success(`Cloned to '${data.name}'`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="et-clone-dialog">
                <DialogHeader><DialogTitle>Clone template</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[11px] text-[#5E636E]">Name your copy</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1"
                            data-testid="et-clone-name"
                            autoFocus
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !name.trim()} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="et-clone-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clone"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
